import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import {
  Form, Input, Button, Checkbox, Row, Col,
} from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { uniq } from 'ramda';

import { upperAndLowerCaseValidator } from '../../helpers/validators';
import {
  signUp, clearSignUp,
} from '../../actions/user';
import { validateAccount } from '../../actions/account';
import PasswordRules from '../PasswordRules';
import Referrer from '../../helpers/referrers';

import c from './SignUpForm.less';

const FormItem = Form.Item;

const layout = {
  left: {
    xs: 24,
    md: 7,
  },
  right: {
    xs: 24,
    md: 17,
  },
  offset: {
    xs: {
      offset: 0,
    },
    md: {
      offset: 7,
    },
  },
};
const passwordRules = [
  {
    required: true, validator: upperAndLowerCaseValidator, message: 'Upper & lowercase letters',
  },
  {
    required: true,
    pattern: /^(?=.*[0-9`~!@#$%^&*()\-_=+[{\]}\\|;:'"<,>./?]).*$/,
    message: 'At least one digit or special symbol',
  },
  {
    required: true, min: 6, message: '6 or more characters',
  },
];

const validateFieldsAsync = (form) => new Promise((resolve, reject) => {
  form.validateFieldsAndScroll((err) => {
    if (!err) return resolve();

    return reject(err);
  });
});

function SignUpForm(props) {
  const { form } = props;
  const { getFieldDecorator, getFieldError, isFieldTouched } = form;
  const [confirmDirty, setConfirmDirty] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [passwordServerErrors, setPasswordServerErrors] = useState(null);
  const history = useHistory();
  const signUpData = useSelector((state) => state.signup);
  const dispatch = useDispatch();

  const recaptchaRef = useRef(null);

  const passwordErrors = typeof getFieldError('password') !== 'undefined' && passwordServerErrors ? (
    <ul>
      {passwordServerErrors.map((error) => (<li key={error}>{error}</li>))}
    </ul>
  ) : null;

  const setValues = (values, errors = {}, clearErrors = false) => {
    const formErrors = form.getFieldsError();

    return Object.keys(values)
      .filter((key) => form.isFieldTouched(key))
      .reduce((acc, key) => {
        const fieldErrors = uniq([
          ...(formErrors[key] || []),
          ...(errors[key] || []),
        ]).map((err) => new Error(err));

        acc[key] = {
          touched: form.isFieldTouched(key),
          value: values[key],
          dirty: true,
          errors: !clearErrors ? fieldErrors : null,
        };

        return acc;
      }, {});
  };

  const getFieldErrors = (field) => {
    const errors = form.getFieldError(field);

    return errors && errors.length > 0 && (
      <ul>
        {
          errors.map((err) => (
            <li key={err}>{err}</li>
          ))
        }
      </ul>
    );
  };

  const getFieldStatus = (name) => {
    if (getFieldErrors(name)) {
      return 'error';
    }
    if (form.isFieldTouched(name)) {
      return 'success';
    }

    return '';
  };

  const getFieldStatusAndErrors = (name) => {
    return {
      help: getFieldErrors(name),
      validateStatus: getFieldStatus(name),
    };
  };

  const compareToFirstPassword = (rule, value, callback) => {
    if (value && value !== form.getFieldValue('password')) {
      callback(new Error('Password and confirmed password must be the same.'));
    } else {
      callback();
    }
  };

  const validateToNextPassword = (rule, value, callback) => {
    if (value && confirmDirty) {
      form.validateFields(['confirmPassword'], { first: true });
    }
    callback();
  };

  const tosValidator = (rule, val, cb) => {
    if (val) {
      cb();
    } else {
      cb(false);
    }
  };

  const clearErrors = (rule, val, cb) => {
    cb();
  };

  const serverValidationRef = useRef(null);

  const _serverValidation = async (field) => {
    const formValues = form.getFieldsValue();

    setPasswordServerErrors(null);

    const postData = {
      ...(Object.keys(formValues).reduce((acc, key) => {
        acc[key] = typeof formValues[key] !== 'undefined' ? formValues[key] : '';
        return acc;
      }, {})),
      validation: undefined,
    };

    if (!serverValidationRef.current) {
      serverValidationRef.current = dispatch(validateAccount(postData, {
        'g-recaptcha-response': formValues.validation,
      }));
    }

    try {
      await serverValidationRef.current;
      const latestFormValues = form.getFieldsValue();

      form.setFields(setValues(latestFormValues, {}, true));
    } catch ({ body }) {
      const latestFormValues = form.getFieldsValue();

      if (body.password && !body.password.errors && form.isFieldTouched('password')) {
        setPasswordServerErrors(body.password);
      }

      if (body.errorCode === 'INVALID_RECAPTCHA_RESPONSE') {
        body.validation = [body.errorMessage]; // eslint-disable-line
        recaptchaRef.current.reset();
      }
      const nextFieldValues = setValues(latestFormValues, body);
      form.setFields(nextFieldValues);

      throw nextFieldValues[field].errors;
    } finally {
      serverValidationRef.current = null;
    }
  };

  useEffect(() => {
    dispatch(clearSignUp());
  }, []);

  useEffect(() => {
    if (signUpData.error) {
      dispatch(clearSignUp());
      const formValues = form.getFieldsValue();
      const fieldValues = setValues(formValues, signUpData.data);
      form.setFields(fieldValues);
    }
  }, [signUpData.error]);

  useEffect(() => {
    if (signUpData.data.success) {
      history.push('/shop/profile');
    }
  }, [signUpData.data.success]);

  const handleConfirmBlur = (e) => {
    const { value } = e.target;
    setConfirmDirty(confirmDirty || !!value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormSubmitting(true);

    const formValues = form.getFieldsValue();

    const postData = {
      ...(Object.keys(formValues).reduce((acc, key) => {
        acc[key] = typeof formValues[key] !== 'undefined' ? formValues[key] : '';
        return acc;
      }, {})),
      validation: undefined,
    };

    let headers = { 'g-recaptcha-response': formValues.validation };

    try {
      await validateFieldsAsync(form);
    } catch (err) {
      setFormSubmitting(false);
      return;
    }

    try {
      await dispatch(validateAccount(postData, headers));

      if (Referrer.site) {
        headers['X-Referrer-Iden'] = Referrer.site;
      }

      if (Referrer.pid) {
        headers = {
          ...headers,
          'X-Affiliate-Id': Referrer.pid,
          'X-Affiliate-SubId': Referrer.subId,
        };
      }

      await dispatch(signUp(postData, headers));

      Referrer.clean();
      history.push('/shop/profile');
    } catch (response) {
      const errors = response.body || response;

      if (errors.password && !errors.password.errors) {
        setPasswordServerErrors(errors.password);
      }

      form.setFields(setValues(formValues, errors));
      setFormSubmitting(false);
    }
  };

  const handleReCaptcha = (response) => {
    form.setFieldsValue({
      validation: response,
    });
  };

  const beValidation = async (rule, value, cb) => {
    const formValues = form.getFieldsValue();

    if (!value || !form.isFieldTouched(rule.field) || !formValues.validation) {
      return cb();
    }

    try {
      await _serverValidation(rule.field);
      cb();
    } catch (errors) {
      cb(errors);
    }
    return null;
  };

  return (
    <Form onSubmit={handleSubmit} className={c.formContainer}>
      <Row>
        <Col {...layout.left} className={c.label}>Username </Col>
        <Col {...layout.right}>
          <FormItem
            hasFeedback
            className={c.input}
            {...getFieldStatusAndErrors('userName')}
          >
            {getFieldDecorator('userName', {
              validateFirst: true,
              validate: [{
                trigger: ['onChange', 'onBlur'],
                rules: [{
                  validator: clearErrors,
                }, {
                  required: true,
                  message: 'Please enter your username',
                }],
              }, {
                trigger: 'onBlur',
                rules: [{
                  validator: beValidation,
                }],
              }],
            })(
              <Input placeholder="Username" />,
            )}
          </FormItem>
        </Col>
      </Row>
      <Row>
        <Col {...layout.left} className={c.label}>Email </Col>
        <Col {...layout.right}>
          <FormItem
            hasFeedback
            {...getFieldStatusAndErrors('userEmail')}
            className={c.input}
          >
            {getFieldDecorator('userEmail', {
              validateFirst: true,
              validate: [{
                trigger: ['onChange', 'onBlur'],
                rules: [{
                  validator: clearErrors,
                }, {
                  required: true,
                  type: 'email',
                  message: 'Please enter a valid email address',
                }],
              }, {
                trigger: 'onBlur',
                rules: [{ validator: beValidation }],
              }],
            })(
              <Input placeholder="Email Address" />,
            )}
          </FormItem>
        </Col>
      </Row>
      <Row>
        <Col {...layout.left} className={c.label}>Password </Col>
        <Col {...layout.right}>
          <FormItem
            hasFeedback
            {...getFieldStatusAndErrors('password')}
            help={passwordErrors}
            className={c.input}
          >
            {getFieldDecorator('password', {
              validate: [{
                trigger: ['onChange', 'onBlur'],
                rules: [
                  ...passwordRules,
                  {
                    validator: validateToNextPassword,
                  },
                ],
              }, {
                trigger: 'onBlur',
                rules: [{ validator: beValidation }],
              }],
            })(
              <Input type="password" placeholder="Password" />,
            )}
          </FormItem>
          <PasswordRules
            isDirty={isFieldTouched('password')}
            errors={getFieldError('password')}
            rules={passwordRules.map((rule) => rule.message)}
          />
        </Col>
      </Row>
      <Row>
        <Col {...layout.left} className={c.label}>Confirm password </Col>
        <Col {...layout.right}>
          <FormItem
            hasFeedback
            {...getFieldStatusAndErrors('confirmPassword')}
            className={c.input}
          >
            {getFieldDecorator('confirmPassword', {
              validateFirst: true,
              validate: [{
                trigger: ['onChange', 'onBlur'],
                rules: [{
                  required: true,
                  message: 'Please confirm your password',
                }, {
                  validator: compareToFirstPassword,
                }],
              }, {
                trigger: 'onBlur',
                rules: [{ validator: beValidation }],
              }],
            })(
              <Input type="password" placeholder="Confirm Password" onBlur={handleConfirmBlur} />,
            )}
          </FormItem>
        </Col>
      </Row>
      <Row>
        <Col {...layout.offset}>
          <FormItem
            {...getFieldStatusAndErrors('tos')}
            className={c.input}
          >
            {getFieldDecorator('tos', {
              valuePropName: 'checked',
              initialValue: false,
              rules: [{
                required: true,
                validator: tosValidator,
                message: 'Please agree with our Terms of Use to continue',
              }],
            })(
              <Checkbox>
                I have read and agree to those{' '}
                <a href="/shop/terms" rel="noreferrer" target="_blank">Terms of Use</a>
              </Checkbox>,
            )}
          </FormItem>
        </Col>
      </Row>
      <Row>
        <Col {...layout.offset}>
          <FormItem
            {...getFieldStatusAndErrors('validation')}
            className={c.input}
          >
            {getFieldDecorator('validation', {
              validateFirst: true,
              rules: [{
                required: true,
                min: 1,
                message: 'Please confirm that you are human',
              }, {
                validator: beValidation,
              }],
            })(
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.RECAPTCHA_CLIENT}
                onChange={handleReCaptcha}
                size="normal"
              />,
            )}
          </FormItem>
        </Col>
      </Row>
      <Row>
        <Col {...layout.offset} className={c.actions}>
          <Button
            className={c.submit}
            type="primary"
            htmlType="submit"
            loading={formSubmitting}
            disabled={formSubmitting}
          >
            Sign up
          </Button>
          <Link to="/shop/login">I already have an account</Link>
        </Col>
      </Row>
    </Form>
  );
}

SignUpForm.propTypes = {
  form: PropTypes.object.isRequired,
};

export default Form.create()(SignUpForm);
